import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { X, Tag, CheckCircle, AlertTriangle } from "lucide-react";
import {
  groupTagsByCategory,
  filterTagsByMessageType,
  tagToTestId,
} from "@/constants/warmUpMergeTags";
import { resolveTagPreview, tagSearchBlob } from "@/utils/warmUpMergeTagPreview";
import { copyToClipboard } from "@/utils/copyToClipboard";

const COPY_NOTICE_MS = 3000;

const TagPreviewBlock = ({ preview, tag }) => {
  const testId = `warm-up-tag-preview-${tagToTestId(tag)}`;

  if (preview.kind === "current") {
    return (
      <div data-testid={testId}>
        <p className="text-[#F3F4F6]/80 text-xs font-body mt-1">
          <span className="text-[#9CA3AF]">Current value: </span>
          {preview.loading ? (
            <span className="italic text-[#9CA3AF]">…</span>
          ) : preview.isEmpty ? (
            <span className="italic text-[#9CA3AF]">Not set</span>
          ) : (
            <span className="break-words">{preview.display}</span>
          )}
        </p>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <p className="text-[#F3F4F6]/80 text-xs font-body mt-1">
        <span className="text-[#9CA3AF]">Example: </span>
        <span className="break-words">{preview.display}</span>
      </p>
      <p className="text-[#9CA3AF]/90 text-xs font-body italic mt-0.5 leading-snug">
        {preview.dynamicNote}
      </p>
    </div>
  );
};

const WarmUpTagPickerModal = ({
  open,
  onClose,
  messageType,
  tags,
  botSettings = null,
  settingsLoading = false,
}) => {
  const [search, setSearch] = useState("");
  const [copyNotice, setCopyNotice] = useState(null);
  const noticeTimeoutRef = useRef(null);

  const clearNoticeTimeout = useCallback(() => {
    if (noticeTimeoutRef.current) {
      clearTimeout(noticeTimeoutRef.current);
      noticeTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSearch("");
      setCopyNotice(null);
      clearNoticeTimeout();
    }
  }, [open, clearNoticeTimeout]);

  useEffect(() => {
    return () => clearNoticeTimeout();
  }, [clearNoticeTimeout]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const handleTagClick = useCallback(
    async (tag) => {
      clearNoticeTimeout();
      const ok = await copyToClipboard(tag);
      setCopyNotice(
        ok
          ? { type: "success", message: "Tag copied to clipboard" }
          : {
              type: "error",
              message: "Could not copy tag. Try again or copy manually.",
            }
      );
      noticeTimeoutRef.current = setTimeout(() => {
        setCopyNotice(null);
        noticeTimeoutRef.current = null;
      }, COPY_NOTICE_MS);
    },
    [clearNoticeTimeout]
  );

  const tagsWithPreview = useMemo(() => {
    const byType = filterTagsByMessageType(tags, messageType);
    return byType.map((item) => ({
      ...item,
      preview: resolveTagPreview(item, botSettings, { settingsLoading }),
    }));
  }, [tags, messageType, botSettings, settingsLoading]);

  const filteredTags = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tagsWithPreview;
    return tagsWithPreview.filter((t) => tagSearchBlob(t, t.preview).includes(q));
  }, [tagsWithPreview, search]);

  const grouped = useMemo(() => groupTagsByCategory(filteredTags), [filteredTags]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="warm-up-tag-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="warm-up-tag-modal-title"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {copyNotice && (
        <div
          className="fixed top-6 right-6 z-[110] animate-in slide-in-from-top-2 duration-300"
          data-testid="warm-up-tag-copy-toast"
          role="status"
        >
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl border ${
              copyNotice.type === "success"
                ? "bg-[#01B47A]/10 border-[#01B47A]/30"
                : "bg-red-500/10 border-red-500/30"
            }`}
          >
            {copyNotice.type === "success" ? (
              <CheckCircle className="w-5 h-5 text-[#01B47A] flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            )}
            <span
              className={`text-sm font-medium font-body ${
                copyNotice.type === "success" ? "text-[#01B47A]" : "text-red-400"
              }`}
            >
              {copyNotice.message}
            </span>
            <button
              type="button"
              onClick={() => {
                clearNoticeTimeout();
                setCopyNotice(null);
              }}
              className="ml-1 p-1 hover:bg-white/10 rounded-lg transition-all text-[#9CA3AF]"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="relative w-full max-w-2xl max-h-[85vh] flex flex-col bg-[#1A1D23] rounded-2xl border border-white/10 shadow-2xl animate-fade-in-up">
        <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#00D1FF]/10">
              <Tag className="w-4 h-4 text-[#00D1FF]" />
            </div>
            <div>
              <h3
                id="warm-up-tag-modal-title"
                className="font-heading font-bold text-lg text-[#F3F4F6]"
              >
                Insert merge tag
              </h3>
              <p className="text-[#9CA3AF] text-xs font-body">
                Click a tag to copy it to your clipboard
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-white/10 transition-all"
            aria-label="Close"
            data-testid="warm-up-tag-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pt-4 pb-2 border-b border-white/10 flex-shrink-0 space-y-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tags…"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50"
            data-testid="warm-up-tag-search"
            autoFocus
          />
          <p className="text-[#9CA3AF] text-xs font-body leading-snug">
            Paste the copied tag into your message text field below. Values from your bot
            settings are shown where available; chat tags use examples — each lead gets
            their own value when sent.
          </p>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {grouped.length === 0 ? (
            <p className="text-[#9CA3AF] text-sm font-body text-center py-8">
              No tags match your search.
            </p>
          ) : (
            grouped.map((section) => (
              <section key={section.categoryId}>
                <h4 className="text-[#F3F4F6] text-xs font-body font-semibold uppercase tracking-wide mb-2">
                  {section.label}
                </h4>
                <ul className="space-y-1">
                  {section.tags.map((item) => (
                    <li key={item.tag}>
                      <button
                        type="button"
                        onClick={() => handleTagClick(item.tag)}
                        className="w-full text-left rounded-lg p-2 -mx-2 hover:bg-white/5 transition-colors group"
                        data-testid={`warm-up-tag-item-${tagToTestId(item.tag)}`}
                      >
                        <code className="text-[#00D1FF] text-xs font-mono break-all">
                          {item.tag}
                        </code>
                        <p className="text-[#9CA3AF] text-xs font-body mt-0.5 leading-snug">
                          {item.description}
                        </p>
                        <TagPreviewBlock preview={item.preview} tag={item.tag} />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default WarmUpTagPickerModal;
